import {
  CoolingOffDetails,
  CoolingOffType,
  Condition,
  Deposit,
  DepositType,
  NoCoolingOffReason,
  SpecialConditionData,
  TimeUnit,
  ValueType,
  useInvalidateUseStaffGetSpecialConditions,
  useStaffGetSpecialConditions,
  useStaffUpdateContractSpecialConditions,
} from '@urbanx/agx.campaigns.client';
import {
  AgxDeposit,
  AgxDatePicker,
  AgxCoolingOffPeriod,
  AgxDropdown,
  AgxFinanceCondition,
  AgxRadioGroup,
  AgxSegmentPicker,
  AgxSpacer,
  AgxTextInput,
  AgxToggleSwitch,
  AgxTypography,
  isFinanceConditionValid,
  toDateOnlyString,
  useToast,
} from '@urbanx/agx.ui-components2';
import { TaskModalPageProps } from 'pages/allTasks/TaskModal/TaskModalPageProps';
import { useEffect, useRef, useState } from 'react';
import { isExchangeInformationTaskType } from 'utils/tasks/exchangeTaskIdentity';
import ContractComplianceSpecialConditions from '../ContractCompliance/ContractComplianceSpecialConditions';
import { prepareCustomSpecialConditionsForSave } from '../contract-information/modals/specialConditions/customSpecialConditionEditor';
import { requiresSalePriceForSplitDollarDeposit } from './exchangeTaskDeposits';
import { useExchangeTaskForm } from './useExchangeTaskForm';

interface ExchangeTaskPageProps extends TaskModalPageProps {
  exchangeForm: ReturnType<typeof useExchangeTaskForm>;
}

const noCoolingOffReasonOptions = [
  {
    label: 'Waived - 66W provided',
    value: NoCoolingOffReason.Waived,
  },
  {
    label: 'Exempt from cooling off',
    value: NoCoolingOffReason.Exempt,
  },
];

const settlementModeSegments = [
  { title: '# days from...', value: 'fromContractDate' },
  { title: 'on..', value: 'specificDate' },
];

const settlementTimeUnitOptions = [
  { label: 'days', value: TimeUnit.Days },
  { label: 'weeks', value: TimeUnit.Weeks },
];

const settlementComponentWidth = '520px';
const settlementDaysInputWidth = '90px';
const settlementTimeUnitWidth = '164px';

const getSettlementMode = (
  settlementInfo: ReturnType<typeof useExchangeTaskForm>['state']['settlementInfo']
) => (settlementInfo?.condition === Condition.OnSpecificDate ? 'specificDate' : 'fromContractDate');

const getCoolingOffDetails = (
  state: ReturnType<typeof useExchangeTaskForm>['state']
): CoolingOffDetails => ({
  coolingOffType: state.coolingOffType,
  coolingOffDays: state.customCoolingOffDays,
  noCoolingOffReason: state.noCoolingOffReason,
});

const useRequestedStepStates = (taskSteps: TaskModalPageProps['task']['taskSteps']) => {
  const requestedStepStates = useRef(new Map<number, boolean>());
  const lastTaskStepsJson = useRef('');
  const taskStepsJson = JSON.stringify(
    taskSteps?.map((step) => ({ order: step.order, isCompleted: step.isCompleted })) ?? []
  );

  if (taskStepsJson !== lastTaskStepsJson.current) {
    lastTaskStepsJson.current = taskStepsJson;
    requestedStepStates.current.clear();
  }

  return requestedStepStates;
};

const syncStep = (
  taskSteps: TaskModalPageProps['task']['taskSteps'],
  toggleTaskStep: TaskModalPageProps['toggleTaskStep'],
  requestedStepStates: Map<number, boolean>,
  order: number,
  isCompleted: boolean
) => {
  const step = taskSteps?.find((candidate) => candidate.order === order);
  if (!step || step.isCompleted === isCompleted) return;

  if (requestedStepStates.get(order) === isCompleted) return;

  requestedStepStates.set(order, isCompleted);
  toggleTaskStep(order, isCompleted);
};

const hasCoolingOffDetails = (state: ReturnType<typeof useExchangeTaskForm>['state']) =>
  !!state.coolingOffType &&
  (state.coolingOffType !== CoolingOffType.NDays ||
    (!!state.customCoolingOffDays && state.customCoolingOffDays > 0)) &&
  (state.coolingOffType !== CoolingOffType.NoCoolingOff || !!state.noCoolingOffReason);

const ExchangeTaskPage = (props: ExchangeTaskPageProps) => {
  const taskType = props.task.category?.taskType;

  if (isExchangeInformationTaskType(taskType)) {
    return <ExchangeInformationPage {...props} />;
  }

  return <ExchangeContractDatesPage {...props} />;
};

const ExchangeInformationPage = (props: ExchangeTaskPageProps) => {
  const { pushToast } = useToast();
  const { registerBeforeTaskModalSave } = props;
  const { state, updateState } = props.exchangeForm;
  const agencyAdminStep = props.task.taskSteps?.find((candidate) => candidate.order === 1);
  const agencyAdminNoted = state.agencyAdminNoted ?? agencyAdminStep?.isCompleted ?? false;
  const requestedStepStates = useRequestedStepStates(props.task.taskSteps);
  const coolingOffDetailsComplete = hasCoolingOffDetails(state);
  const hasDepositValue =
    !!state.deposit?.isDepositSelected && (!!state.deposit.percent || !!state.deposit.amount);
  const depositDefaultValue: Deposit = {
    ...state.deposit,
    depositType: DepositType.Initial,
    depositValueType: state.deposit?.depositValueType ?? ValueType.Percentage,
    isDepositSelected: state.deposit?.isDepositSelected ?? false,
  };

  const onDepositChanged = ({ value }: { id: string; value: Deposit }) => {
    if (!value.isDepositSelected && !state.deposit?.isDepositSelected) return;

    updateState({
      deposit: {
        ...value,
        depositType: DepositType.Initial,
        isDepositSelected: value.isDepositSelected ?? true,
      },
    });
  };

  useEffect(() => {
    const stepStates = [
      [1, agencyAdminNoted],
      [2, !!state.contractDate],
      [3, !!state.salePrice && state.salePrice > 0],
      [4, hasDepositValue],
      [5, coolingOffDetailsComplete],
      [6, !!state.depositPayableOption],
    ] as const;

    for (const [order, isCompleted] of stepStates) {
      syncStep(
        props.task.taskSteps,
        props.toggleTaskStep,
        requestedStepStates.current,
        order,
        isCompleted
      );
    }
  }, [
    agencyAdminNoted,
    coolingOffDetailsComplete,
    hasDepositValue,
    props.task.taskSteps,
    props.toggleTaskStep,
    requestedStepStates,
    state.contractDate,
    state.depositPayableOption,
    state.salePrice,
  ]);

  useEffect(() => {
    if (
      state.coolingOffType !== CoolingOffType.NoCoolingOff ||
      state.depositPayableOption === 'full'
    ) {
      return;
    }

    updateState({ depositPayableOption: 'full' });
  }, [state.coolingOffType, state.depositPayableOption, updateState]);

  useEffect(() => {
    registerBeforeTaskModalSave?.(() => {
      if (!requiresSalePriceForSplitDollarDeposit(state)) return true;

      pushToast({
        type: 'error',
        message: 'Enter the sale price before saving a split dollar deposit',
      });
      return false;
    });

    return () => registerBeforeTaskModalSave?.(undefined);
  }, [pushToast, registerBeforeTaskModalSave, state]);

  return (
    <AgxSpacer direction="vertical" fill gap="spacing-2xl">
      <AgxTypography
        id="exchange-information-title"
        type="label"
        size="large"
        color="var(--text-primary)"
      >
        Step {props.taskNumber}: Contract Information
      </AgxTypography>

      <AgxSpacer
        direction="horizontal"
        contentJustification="space-between"
        contentAlignment="center"
        fill
      >
        <AgxTypography id="agency-admin-noted" type="body_ui" size="medium">
          Confirm Agency Admin is noted on the Contract
        </AgxTypography>
        <AgxToggleSwitch
          id="agency-admin-noted-toggle"
          checked={agencyAdminNoted}
          onChange={(agencyAdminNoted) => updateState({ agencyAdminNoted })}
        />
      </AgxSpacer>

      <AgxDatePicker
        id="exchange-contract-date"
        label="Contract Date"
        optionalText=""
        stretch
        value={state.contractDate ? toDateOnlyString(state.contractDate) : ''}
        onChange={(value) => updateState({ contractDate: value ? new Date(value) : undefined })}
      />

      <AgxTextInput
        id="exchange-sale-price"
        label="Sale Price"
        type="currency"
        stretch
        hideOptionalText
        addTrailingZeros={false}
        defaultValue={state.salePrice?.toString() ?? ''}
        onChange={(value) => updateState({ salePrice: value ? Number(value) : undefined })}
      />

      <AgxDeposit
        id="exchange-deposit"
        label="Deposit"
        defaultValue={depositDefaultValue}
        onValueChanged={onDepositChanged}
        salePrice={state.salePrice ?? 0}
        showDepositDropdownOption={false}
        required
      />

      <AgxCoolingOffPeriod
        id="exchange-cooling-off-period"
        label="Cooling Off Period"
        layout="stacked"
        defaultValue={getCoolingOffDetails(state)}
        options={[
          { label: '5 days', value: CoolingOffType.FiveDays },
          { label: '10 days', value: CoolingOffType.TenDays },
          { label: '___ days', value: CoolingOffType.NDays },
          {
            label: 'No cooling off period',
            value: CoolingOffType.NoCoolingOff,
          },
        ]}
        noCoolingOffReasonLabel="Reason:"
        noCoolingOffReasonOptions={noCoolingOffReasonOptions}
        onValueChanged={({ value }) => {
          updateState({
            coolingOffType: value.coolingOffType,
            customCoolingOffDays: value.coolingOffDays,
            noCoolingOffReason: value.noCoolingOffReason,
            ...(value.coolingOffType === CoolingOffType.NoCoolingOff
              ? { depositPayableOption: 'full' }
              : {}),
          });
        }}
      />

      {state.coolingOffType !== CoolingOffType.NoCoolingOff && (
        <AgxRadioGroup
          id="exchange-deposit-payable"
          label="Deposit Payable"
          layout="stacked"
          value={state.depositPayableOption}
          options={[
            { label: '.25% paid upfront', value: 'quarter' },
            { label: 'Full deposit paid upfront', value: 'full' },
          ]}
          onChange={(depositPayableSelection) =>
            updateState({
              depositPayableOption: depositPayableSelection === 'full' ? 'full' : 'quarter',
            })
          }
        />
      )}
    </AgxSpacer>
  );
};

const ExchangeContractDatesPage = (props: ExchangeTaskPageProps) => {
  const { state, updateState } = props.exchangeForm;
  const contractId = props.task.contractId ?? '';
  const campaignId = props.campaign.campaignId ?? '';
  const { pushToast } = useToast();
  const hasHandledInitialSettlementSegment = useRef(false);
  const requestedStepStates = useRequestedStepStates(props.task.taskSteps);
  const [localSpecialConditions, setLocalSpecialConditions] = useState<SpecialConditionData[]>([]);

  const { data: specialConditionsResponse } = useStaffGetSpecialConditions(campaignId, contractId);
  const invalidateSpecialConditions = useInvalidateUseStaffGetSpecialConditions();
  const { staffUpdateContractSpecialConditions } = useStaffUpdateContractSpecialConditions({
    onSuccess: () => {},
    onError: () => {},
  });

  useEffect(() => {
    setLocalSpecialConditions(specialConditionsResponse?.specialConditionsData ?? []);
  }, [specialConditionsResponse?.specialConditionsData]);

  useEffect(() => {
    const settlementMode = getSettlementMode(state.settlementInfo);
    const hasSettlement =
      settlementMode === 'specificDate'
        ? !!state.settlementInfo?.referenceDate
        : !!state.settlementInfo?.daysFromDate && state.settlementInfo.daysFromDate > 0;
    const hasFinanceCondition = isFinanceConditionValid(state.subjectToFinance);

    syncStep(
      props.task.taskSteps,
      props.toggleTaskStep,
      requestedStepStates.current,
      1,
      hasFinanceCondition
    );
    syncStep(
      props.task.taskSteps,
      props.toggleTaskStep,
      requestedStepStates.current,
      2,
      hasSettlement
    );
  }, [
    props.task.taskSteps,
    props.toggleTaskStep,
    requestedStepStates,
    state.settlementInfo,
    state.subjectToFinance,
  ]);

  const updateSpecialConditions = async (updatedConditions: SpecialConditionData[]) => {
    const preparedSpecialConditions = prepareCustomSpecialConditionsForSave(updatedConditions);

    try {
      const response = await staffUpdateContractSpecialConditions({
        contractId,
        body: preparedSpecialConditions,
      });
      if (!response?.success) {
        pushToast({
          type: 'error',
          message: response?.errors?.join(', ') || 'Special conditions were not valid',
        });
        return false;
      }
      if (!response.specialConditions?.specialConditionsData) {
        throw new Error('Canonical special conditions are required');
      }

      void invalidateSpecialConditions(campaignId, contractId);
      setLocalSpecialConditions(response.specialConditions.specialConditionsData);
      return true;
    } catch {
      pushToast({
        type: 'error',
        message: 'Failed to update special conditions',
      });
      return false;
    }
  };

  const onSettlementSelectionChange = (settlementSelection: string) => {
    if (!hasHandledInitialSettlementSegment.current) {
      hasHandledInitialSettlementSegment.current = true;
      return;
    }

    const nextSettlementSelection =
      settlementSelection === 'specificDate' ? 'specificDate' : 'fromContractDate';
    const currentSettlementSelection = getSettlementMode(state.settlementInfo);

    if (nextSettlementSelection === currentSettlementSelection) return;

    updateState({
      settlementInfo:
        nextSettlementSelection === 'specificDate'
          ? {
              ...state.settlementInfo,
              condition: Condition.OnSpecificDate,
              daysFromDate: undefined,
              referenceDate: undefined,
            }
          : {
              ...state.settlementInfo,
              condition: Condition.NdaysFromDate,
              daysFromDate: undefined,
              referenceDate: undefined,
              fromDateTimeUnit: state.settlementInfo?.fromDateTimeUnit ?? TimeUnit.Days,
            },
    });
  };
  const settlementMode = getSettlementMode(state.settlementInfo);

  return (
    <AgxSpacer direction="vertical" fill gap="spacing-2xl">
      <AgxTypography
        id="exchange-dates-title"
        type="label"
        size="large"
        color="var(--text-primary)"
      >
        Step {props.taskNumber}: Contract Dates
      </AgxTypography>

      <AgxFinanceCondition
        id="exchange-finance"
        label="Finance"
        defaultValue={state.subjectToFinance}
        validate
        specialConditions={localSpecialConditions}
        campaignId={campaignId}
        defaultToNotSubjectToFinance={false}
        defaultSubjectToFinanceCondition={Condition.OnSpecificDate}
        conditionsToIncludeInDaysFrom={[]}
        conditionsToIncludeInOn={[Condition.OnSpecificDate]}
        includeContractDateTypeInOnOptions={false}
        onValueChanged={({ value }) => updateState({ subjectToFinance: value })}
      />

      <AgxSpacer
        direction="vertical"
        gap="spacing-xl"
        style={{ width: settlementComponentWidth, maxWidth: '100%' }}
      >
        <AgxTypography
          id="exchange-settlement-label"
          type="label"
          size="medium"
          color="var(--text-primary)"
        >
          Settlement Date
        </AgxTypography>

        <AgxSegmentPicker
          key={`exchange-settlement-mode-${settlementMode}`}
          id="exchange-settlement-mode"
          segments={settlementModeSegments}
          defaultValue={settlementMode}
          onChange={onSettlementSelectionChange}
        />

        {settlementMode === 'specificDate' ? (
          <AgxDatePicker
            id="exchange-settlement-date"
            noHeader
            stretch
            value={
              state.settlementInfo?.referenceDate
                ? toDateOnlyString(state.settlementInfo.referenceDate)
                : ''
            }
            onChange={(value) =>
              updateState({
                settlementInfo: {
                  ...state.settlementInfo,
                  condition: Condition.OnSpecificDate,
                  referenceDate: toDateOnlyString(value),
                },
              })
            }
          />
        ) : (
          <AgxSpacer
            direction="horizontal"
            gap="xs"
            contentAlignment="baseline"
            style={{
              flexWrap: 'nowrap',
              width: 'max-content',
              maxWidth: '100%',
            }}
          >
            <AgxSpacer flexShrink={0} style={{ width: settlementDaysInputWidth }}>
              <AgxTextInput
                id="exchange-settlement-days"
                type="number"
                noHeader
                maxLength={3}
                stretch
                defaultValue={state.settlementInfo?.daysFromDate?.toString() ?? ''}
                onChange={(value) =>
                  updateState({
                    settlementInfo: {
                      ...state.settlementInfo,
                      condition: Condition.NdaysFromDate,
                      daysFromDate: value ? Number(value) : undefined,
                    },
                  })
                }
              />
            </AgxSpacer>
            <AgxSpacer flexShrink={0} style={{ width: settlementTimeUnitWidth }}>
              <AgxDropdown
                id="exchange-settlement-time-unit"
                options={settlementTimeUnitOptions}
                selected={state.settlementInfo?.fromDateTimeUnit ?? TimeUnit.Days}
                noHeader
                onChange={(settlementTimeUnit) =>
                  updateState({
                    settlementInfo: {
                      ...state.settlementInfo,
                      condition: Condition.NdaysFromDate,
                      fromDateTimeUnit: settlementTimeUnit as TimeUnit,
                    },
                  })
                }
              />
            </AgxSpacer>
            <AgxTypography
              id="exchange-settlement-from-contract-date"
              type="body_ui"
              size="medium"
              color="var(--text-primary)"
              noWrap
              style={{ flexShrink: 0 }}
            >
              from Contract Date
            </AgxTypography>
          </AgxSpacer>
        )}
      </AgxSpacer>

      <AgxSpacer direction="vertical" fill gap="spacing-md">
        <ContractComplianceSpecialConditions
          {...props}
          addButtonVariant="primary"
          specialConditions={localSpecialConditions}
          onSpecialConditionsUpdated={updateSpecialConditions}
        />
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default ExchangeTaskPage;
